import { SocketIOEventEnum } from '@zaoblako/shared/common/enums/socketIO/socket.io.event.enum';
import { SocketIOEventTypeEnum } from '@zaoblako/shared/common/enums/socketIO/socket.io.event.type.enum';
import type { TelemetryPageHitFormInterface } from '@zaoblako/shared/common/dtos/telemetry-page-hit-form.dto';
import type { ClientToServerEvents, ServerToClientEvents } from '@zaoblako/shared/common/interfaces/socket.io.event.interface';

import { Injectable } from '@angular/core';
import { NavigationEnd, Router, type ActivatedRouteSnapshot } from '@angular/router';
import { io, type Socket } from 'socket.io-client';
import { filter } from 'rxjs/operators';

/** Page hit params placed in route `data` (timestamp is set automatically). */
export type TelemetryPageHitParams = Omit<TelemetryPageHitFormInterface, 'created'>;

const PAGE_HIT_ROUTE_DATA_KEY = 'pageHit';

/** Spread into route `data` to start tracking visits of that route. */
export const telemetryPageHit = (params: TelemetryPageHitParams): { [PAGE_HIT_ROUTE_DATA_KEY]: TelemetryPageHitParams } => ({
  [PAGE_HIT_ROUTE_DATA_KEY]: params
});

@Injectable({ providedIn: 'root' })
export class TelemetryService {
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | undefined;

  private authToken: string | undefined;

  private hitsAccumulator: TelemetryPageHitFormInterface[] = [];

  private lastLoggedPath: string | undefined;

  public constructor(private readonly router: Router) {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.trackNavigation(event.urlAfterRedirects));

    if (this.router.navigated) {
      this.trackNavigation(this.router.url);
    }
  }

  /**
   * Connect to telemetry chanel socket
   *
   * @param authToken User auth token
   */
  public connect(authToken: string): void {
    if (!authToken) {
      return;
    }

    if (this.socket && this.authToken === authToken) {
      return;
    }

    this.closeSocket();
    this.authToken = authToken;

    this.socket = io(SocketIOEventTypeEnum.TELEMETRY, {
      path: '/api/socket/connect',
      timeout: 10 * 60 * 1000,
      auth: { token: authToken },
      withCredentials: true,
      multiplex: false
    });

    this.socket.on(SocketIOEventEnum.CONNECT, () => {
      this.flushAccumulator();
    });

    this.socket.on(SocketIOEventEnum.DISCONNECT, (reason) => {
      if (reason === 'io server disconnect') {
        this.socket?.connect();
      }
    });
  }

  /**
   * Disconnect from telemetry chanel socket
   */
  public disconnect(): void {
    this.hitsAccumulator = [];
    this.lastLoggedPath = undefined;
    this.closeSocket();
    this.authToken = undefined;
  }

  /**
   * Send one or more page hits to telemetry
   *
   * @param [hits] Page hits to send
   */
  public logPageHits(...hits: Array<TelemetryPageHitFormInterface | TelemetryPageHitParams>): void {
    if (!hits?.length) {
      return;
    }

    const payload: TelemetryPageHitFormInterface[] = hits.map((hit) => ({
      created: 'created' in hit && hit.created ? hit.created : new Date(),
      module: hit.module,
      section: hit.section,
      tab: hit.tab,
      extraTag: hit.extraTag
    }));

    if (!this.socket?.connected) {
      this.hitsAccumulator.push(...payload);
      return;
    }

    this.socket.emit(SocketIOEventEnum.TELEMETRY_PAGE_HIT, payload);
  }

  private trackNavigation(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    if (path === this.lastLoggedPath) {
      return;
    }

    this.lastLoggedPath = path;

    const pageHit = this.resolvePageHit(this.router.routerState.snapshot.root);
    if (!pageHit) {
      return;
    }

    this.logPageHits(pageHit);
  }

  private resolvePageHit(route: ActivatedRouteSnapshot): TelemetryPageHitParams | undefined {
    let current: ActivatedRouteSnapshot | null = route;
    let pageHit: TelemetryPageHitParams | undefined;

    while (current) {
      const candidate = current.data?.[PAGE_HIT_ROUTE_DATA_KEY] as TelemetryPageHitParams | undefined;
      if (candidate?.module && candidate?.section && candidate?.tab) {
        pageHit = candidate;
      }
      current = current.firstChild;
    }

    return pageHit;
  }

  private closeSocket(): void {
    if (!this.socket) {
      return;
    }

    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = undefined;
  }

  private flushAccumulator(): void {
    if (!this.hitsAccumulator.length || !this.socket?.connected) {
      return;
    }

    const hits = this.hitsAccumulator;
    this.hitsAccumulator = [];
    this.socket.emit(SocketIOEventEnum.TELEMETRY_PAGE_HIT, hits);
  }
}
